import { Checkbox } from '@opendoor/atoms/checkbox';

interface PopoverCheckboxOption<Value extends string> {
  label: string;
  value: Value;
}

interface PopoverCheckboxOptionsProps<Value extends string> {
  'aria-label': string;
  analyticsName: string;
  onCheckedChange: (option: Value, checked: boolean) => void;
  options: readonly PopoverCheckboxOption<Value>[];
  values: readonly Value[];
}

/**
 * Multi-select popover option list. The sole remaining checked option is
 * disabled, so the selection can never empty and one option is always active.
 */
export const PopoverCheckboxOptions = <Value extends string>({
  'aria-label': ariaLabel,
  analyticsName,
  onCheckedChange,
  options,
  values,
}: PopoverCheckboxOptionsProps<Value>) => {
  return (
    <div role="group" aria-label={ariaLabel} className="flex flex-col gap-1">
      {options.map((option) => {
        const checked = values.includes(option.value);
        const inputId = `availability-option-${option.value}`;
        return (
          <div
            key={option.value}
            className="flex w-full items-center justify-between rounded-lg border-0 bg-transparent px-3 py-2 text-left text-body-sm text-od-content-primary hover:bg-od-bg-hover-primary"
          >
            <label htmlFor={inputId} className="cursor-pointer">
              {option.label}
            </label>
            <Checkbox
              id={inputId}
              analyticsName={analyticsName}
              name={option.value}
              value={option.value}
              checked={checked}
              disabled={checked && values.length === 1}
              onCheckedChange={(nextChecked) => onCheckedChange(option.value, nextChecked === true)}
            />
          </div>
        );
      })}
    </div>
  );
};
